import { useState, useEffect } from "preact/hooks"
import { Button } from "../../../components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"
import {
  format_forecast_delta_currency,
  format_plan_total_currency,
  type PlanTotalsSnapshot,
} from "../utils/plan_totals_snapshot"
import type { UnsavedDiff } from "../utils/unsaved_diff"
import { UnsavedChangesList } from "./unsaved_changes_list"

interface ForecastSaveConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  is_saving: boolean
  previous_totals: PlanTotalsSnapshot | null
  current_totals: PlanTotalsSnapshot
  default_driver?: string
  /** A sales rep saving the primary or a locked version: the save is queued for an admin, not written. */
  requires_approval?: boolean
  /** Exactly what the save writes, cell by cell; listed so the planner reads it before saving. */
  unsaved_diff?: UnsavedDiff | null
  onConfirm: (driver: string, notes: string) => void | Promise<void>
}

export function ForecastSaveConfirmDialog(props: ForecastSaveConfirmDialogProps) {
  const { open, onOpenChange, is_saving, previous_totals, current_totals, default_driver, requires_approval, unsaved_diff, onConfirm } = props
  const [driver, set_driver] = useState("")
  const [notes, set_notes] = useState("")

  useEffect(() => {
    if (!open) {
      return
    }
    set_driver(default_driver?.trim() ? default_driver.trim() : "")
    set_notes("")
  }, [open, default_driver])

  const delta_total =
    previous_totals != null ? current_totals.total_dollars - previous_totals.total_dollars : null

  async function handle_submit(): Promise<void> {
    const d = driver.trim()
    if (!d) {
      return
    }
    await onConfirm(d, notes.trim())
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg gap-4">
        <DialogHeader>
          <DialogTitle>Save forecast changes</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm text-gray-700">
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
            <div className="font-medium text-gray-900">Overall plan total (plan columns)</div>
            {previous_totals != null ? (
              <ul className="mt-2 list-inside list-disc space-y-1 text-xs text-gray-600">
                <li>Previous: {format_plan_total_currency(previous_totals.total_dollars)}</li>
                <li>After your edits: {format_plan_total_currency(current_totals.total_dollars)}</li>
                <li className="list-none font-semibold text-gray-900">
                  Change: {format_forecast_delta_currency(delta_total ?? 0)}
                </li>
              </ul>
            ) : (
              <p className="mt-2 text-xs text-gray-600">
                New total: {format_plan_total_currency(current_totals.total_dollars)} (no prior baseline in this
                session).
              </p>
            )}
          </div>
          {unsaved_diff ? (
            <div className="space-y-1.5">
              <div className="font-medium text-gray-900">
                What this save changes ({(unsaved_diff.rows.length + unsaved_diff.cell_count).toLocaleString()})
              </div>
              <UnsavedChangesList diff={unsaved_diff} />
            </div>
          ) : null}
          <div className="space-y-1.5">
            <Label htmlFor="forecast-save-driver">Driver of this change (required)</Label>
            <Input
              id="forecast-save-driver"
              value={driver}
              onInput={(e: Event) => set_driver((e.target as HTMLInputElement).value)}
              placeholder="e.g. Updated ACV after retailer feedback"
              disabled={is_saving}
            />
            <p className="text-xs text-gray-500">This appears in the changelog with your name.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="forecast-save-notes">Notes (optional)</Label>
            <textarea
              id="forecast-save-notes"
              value={notes}
              onInput={(e: Event) => set_notes((e.target as HTMLTextAreaElement).value)}
              rows={3}
              className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Extra detail for the audit log…"
              disabled={is_saving}
            />
          </div>
        </div>
        {requires_approval ? (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            This version is the primary or locked. Your change goes to the org admins for approval and is
            applied once one of them approves it; nothing changes on the version until then.
          </p>
        ) : null}
        <div className="flex justify-end gap-2 border-t pt-4">
          <Button type="button" variant="outline" disabled={is_saving} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={is_saving || !driver.trim()} onClick={() => void handle_submit()}>
            {is_saving ? (requires_approval ? "Sending…" : "Saving…") : requires_approval ? "Send for approval" : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
